import React from 'react';
import { BrowserRouter } from 'react-router-dom';

import { Refine } from '@refinedev/core';
import routerProvider, { DocumentTitleHandler, UnsavedChangesNotifier } from '@refinedev/react-router-v6';

import { KitProvider, SnackbarProvider } from '@rrexpress/core';

import { resources } from '@/config/resources';
import { BaseContextProvider } from '@/context/base-list-context';
import { LayoutProvider } from '@/context/layout-context';
import { ModalProvider } from '@/context/modal';
import dataProvider from '@/providers/dataProvider';
import { axiosInstance } from '@/utility';
import { toTitleCaseWithoutDashes } from '@/utility/helper/common';

import { ErrorBoundary } from '../components/error-boundary/error-boundary';
import accessControlProvider from '../providers/accessControlProvider';
import authProvider from '../providers/authProvider';
import AppRoutes from './router';

import '@/assets/css/index.css';

const apiUrl = `${import.meta.env.VITE_API_BASE_URL}/api/customer-portal/v1`;
const authUrl = `${import.meta.env.VITE_API_AUTH_URL}/api/customer-portal/v1`;
const masterdataUrl = `${import.meta.env.VITE_API_MASTERDATA_URL}/api/customer-portal/v1`;
const awbUrl = `${import.meta.env.VITE_API_AWB_URL}/api/customer-portal/v1`;
const reportUrl = `${import.meta.env.VITE_API_REPORT_URL}/api/customer-portal/v1`;

const BuildProviderTree = (providers: any[]): any => {
  if (providers.length === 1) {
    return providers[0];
  }

  const A = providers.shift();
  const B = providers.shift();
  return BuildProviderTree([
    ({ children }: React.PropsWithChildren) => (
      <A>
        <B>
          {children}
        </B>
      </A>
    ),
    ...providers,
  ]);
};

const App: React.FC = () => {
  const defaultDataProvider = dataProvider(apiUrl, axiosInstance);
  const authDataProvider = dataProvider(authUrl, axiosInstance);
  const masterdataDataProvider = dataProvider(masterdataUrl, axiosInstance);
  const awbaDataProvider = dataProvider(awbUrl, axiosInstance);
  const reportDataProvider = dataProvider(reportUrl, axiosInstance);

  const Providers = BuildProviderTree([
    KitProvider,
    SnackbarProvider,
    LayoutProvider,
    ModalProvider,
  ]);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <BaseContextProvider>
          <Refine
            authProvider={authProvider}
            dataProvider={{
              default: defaultDataProvider,
              auth: authDataProvider,
              masterdata: masterdataDataProvider,
              awb: awbaDataProvider,
              report: reportDataProvider,
            }}
            routerProvider={routerProvider}
            resources={resources}
            accessControlProvider={accessControlProvider}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
            }}
          >
            <Providers>
              <>
                <AppRoutes />
                <UnsavedChangesNotifier />
                <DocumentTitleHandler
                  handler={({ action, resource }) => {
                    const actionPrefixMatcher = {
                      create: `Create new ${toTitleCaseWithoutDashes(resource?.name || '')}`,
                      clone: `Clone ${toTitleCaseWithoutDashes(resource?.name || '')}`,
                      edit: `Edit ${toTitleCaseWithoutDashes(resource?.name || '')}`,
                      show: `Show ${toTitleCaseWithoutDashes(resource?.name || '')}`,
                      list: `${toTitleCaseWithoutDashes(resource?.name || '')}`,
                    };
                    const suffix = ' | Bluebird Kirim';
                    const title = actionPrefixMatcher[action || 'list'] + suffix;

                    return title;
                  }}
                />
              </>
            </Providers>
          </Refine>
        </BaseContextProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
